<template>
	<div class="guide-page">
		<!-- 页头 -->
		<div class="guide-hero">
			<div class="guide-hero__icon"><i class="el-icon-notebook-2"></i></div>
			<div class="guide-hero__text">
				<div class="guide-hero__title">系统使用说明</div>
				<div class="guide-hero__sub">这里用大白话讲清楚系统里的重要规则：销售订单、会员卡销、充值欠款、退卡和提成，以及门店申购单、商品调拨。每条规则下面都配了例子；最后一节是「常见提示速查表」，遇到系统弹提示不知道怎么回事，可以直接去那里对号入座。</div>
			</div>
		</div>

		<div class="guide-body">
			<!-- 左侧目录 -->
			<div ref="tocBox" class="guide-toc">
				<div class="guide-toc__title">目录</div>
				<div
					v-for="(sec, i) in sections"
					:key="sec.id"
					class="guide-toc__item"
					:class="{ 'guide-toc__item--active': activeSection === sec.id }"
					@click="scrollTo(sec.id)"
				>
					<span class="guide-toc__num">{{ i + 1 }}</span>
					<span>{{ sec.title }}</span>
				</div>
				<div class="guide-toc__backtop" @click="backToTop">
					<i class="el-icon-top"></i>
					<span>回到顶部</span>
				</div>
			</div>

			<!-- 正文：高度随窗口自适应，内容超出时本区域自己滚动 -->
			<div ref="contentBox" class="guide-content">
				<div v-for="sec in sections" :id="sec.id" :key="sec.id" class="guide-section">
					<div class="guide-section__head">
						<span class="guide-section__no">{{ sec.no }}</span>
						<span class="guide-section__title">{{ sec.title }}</span>
					</div>

					<div v-for="(item, j) in sec.items" :key="j" class="guide-clause">
						<div class="guide-clause__rule">
							<i class="el-icon-circle-check guide-clause__icon"></i>
							<span>{{ item.rule }}</span>
						</div>
						<div v-for="(ex, k) in item.examples" :key="k" class="guide-clause__example">
							<span class="guide-clause__example-tag">举个例子</span>
							<span>{{ ex }}</span>
						</div>
					</div>

					<!-- 常见提示语速查表 -->
					<div v-if="sec.table" class="guide-table">
						<div class="guide-table__row guide-table__row--head">
							<div class="guide-table__cell guide-table__cell--no">序号</div>
							<div class="guide-table__cell guide-table__cell--msg">系统提示</div>
							<div class="guide-table__cell">是什么意思 / 怎么处理</div>
						</div>
						<div v-for="(row, r) in sec.table" :key="r" class="guide-table__row">
							<div class="guide-table__cell guide-table__cell--no">{{ r + 1 }}</div>
							<div class="guide-table__cell guide-table__cell--msg">{{ row.msg }}</div>
							<div class="guide-table__cell">{{ row.how }}</div>
						</div>
					</div>

					<div v-if="sec.tip" class="guide-section__tip">
						<i class="el-icon-info"></i>
						<span>{{ sec.tip }}</span>
					</div>
				</div>

				<div class="guide-footer">
					本说明对应 2026 年 8 月 30 日版本的业务规则，如与系统实际提示不一致，以系统提示为准；如有疑问请联系管理员。
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "UsageGuide",
	data() {
		return {
			activeSection: "sec-order",
			// 点击目录触发的平滑滚动期间，暂停「由滚动位置反推高亮」，避免高亮乱跳
			isManualScroll: false,
			// scroll 事件节流开关（一帧最多算一次）
			scrollTicking: false,
			// 手动跳转的延时器句柄
			manualTimer: null,
			sections: [
				{
					id: "sec-order",
					no: "01",
					title: "销售订单的基本规则",
					items: [
						{
							rule: "订单要先「确认」才算真正成交。刚开出来的订单只是记录，不扣钱、不计入当天业绩；在销售订单列表里确认之后，才会扣款、算业绩、算提成。",
							examples: [
								"下午给顾客开了一笔 300 元的订单但一直没确认，顾客的会员卡不会被扣钱；晚上店长确认订单后，才真正扣款，订单才算完成。"
							]
						},
						{
							rule: "同一种支付方式，一笔订单只能用一次。想组合付款没问题，但每种方式只能出现一笔。",
							examples: [
								"一笔 300 元的订单，不能用会员卡刷两次；但可以「现金 100 元 + 会员卡 200 元」这样组合支付。"
							]
						},
						{
							rule: "会员卡销，就是直接用会员卡里的余额支付货款。一笔订单最多用一次卡销。",
							examples: [
								"顾客卡里有 500 元，买 300 元的商品时选择卡销，确认后卡里剩 200 元。"
							]
						},
						{
							rule: "这个会员只要还有没确认的订单（不管当初是用什么方式付的），就不能再开新的卡销单。",
							examples: [
								"昨天用现金开了一笔订单一直没确认，今天想用会员卡给同一位顾客开单 → 系统会提示「会员存在未完成订单」，先去把昨天那笔确认掉。"
							]
						},
						{
							rule: "同一个会员，当天已经开过一笔金额相同、并且还没确认的订单时，新订单开不出来。这是为了防止重复开单。",
							examples: [
								"上午开了一笔 500 元的订单没确认，下午又给同一位顾客开一笔 500 元 → 系统会提示「会员存在相同金额的未完成订单」；把上午那笔确认掉就好了，或者把金额改成不一样的。"
							]
						},
						{
							rule: "改订单的时候，上面这些检查全都要再过一遍。想先开一张现金单、之后再改成卡销来绕过，是行不通的。",
							examples: []
						}
					],
					tip: "开单前先想想：这个会员名下有没有还没确认的单子？有就先处理掉。"
				},
				{
					id: "sec-twostep",
					no: "02",
					title: "会员卡销的「两步走」",
					items: [
						{
							rule: "下单时：系统只检查卡里的钱够不够，不会真的扣钱。这时候把订单删掉，不会有任何影响。",
							examples: []
						},
						{
							rule: "下单之后、确认之前，这笔钱相当于被「预定」了。同一张会员卡，同一时间只能有一笔这样还没确认的卡销订单。",
							examples: [
								"上午用卡开了一笔 800 元的卡销单还没确认，下午想再开一笔卡销单 → 系统会提示「该会员卡存在未确认的卡销订单【单号】」，把上一笔处理完才能继续。",
								"不同会员的不同卡互不影响：A 顾客的卡上挂着一笔未确认卡销单，不影响 B 顾客用他自己的卡开单。"
							]
						},
						{
							rule: "想解除这个「占位」，有三条路：把上一笔确认掉、把上一笔订单删掉、或者把上一笔的卡销改成别的支付方式。",
							examples: [
								"最快的办法是去销售订单列表找到那笔单子，确认掉它；如果确实是开错了单，直接删除也可以。"
							]
						},
						{
							rule: "确认时：才真正从卡里扣钱、记一笔消费。如果确认前卡又被花过，钱不够就会确认失败。",
							examples: [
								"上午用余额 1000 元的卡下了 800 元的卡销单（还没扣款）；中午这张卡又消费了 500 元；晚上确认订单时卡里只剩 500 元，不够 800 元，确认会失败，需要先让会员充值或者调整订单金额。"
							]
						},
						{
							rule: "确认失败不用慌：订单数据不会错乱，补足卡余额后重新确认即可。",
							examples: []
						}
					],
					tip: "简单记：下单=「占个位」（一张卡同时只能占一个），确认=「真扣钱」。"
				},
				{
					id: "sec-arrears",
					no: "03",
					title: "有欠款时，卡里「能花的钱」会变少",
					items: [
						{
							rule: "什么是欠款：充值时钱没收齐，先给会员把充值记到卡上，就产生一笔「欠款的充值订单」。",
							examples: []
						},
						{
							rule: "如果这张卡有欠款，能用来卡销的「可用余额」= 卡余额 − 欠款金额 − 那笔充值的赠送金额。没有欠款的卡，可用余额就是卡余额本身。",
							examples: [
								"卡余额 2000 元，其中有一笔充值还欠着 800 元、当时赠送了 200 元 → 现在实际最多只能卡销 2000 − 800 − 200 = 1000 元。",
								"一张没有任何欠款的卡，余额 1000 元 → 可用余额就是 1000 元。"
							]
						},
						{
							rule: "为什么要这样算：欠款的充值是「先记账、后收钱」，卡里的余额有一部分其实来自还没收到的钱，这部分（连同赠送）不能先花掉。",
							examples: []
						},
						{
							rule: "欠款按「最近那一笔」充值订单来计算。因为一张卡同时最多只会有一笔欠款，不存在多笔叠加的情况。",
							examples: []
						},
						{
							rule: "下卡销单时看的是可用余额，确认订单时还要再看一次可用余额。中间这段时间会员去别的地方消费过，也可能导致确认失败。",
							examples: []
						}
					],
					tip: "卡余额 ≠ 能花的钱。有欠款时，能花的钱会少一截。"
				},
				{
					id: "sec-recharge",
					no: "04",
					title: "会员充值与欠款规则",
					items: [
						{
							rule: "充值要确认后才到账，赠送的金额也是确认后才一起进卡。",
							examples: []
						},
						{
							rule: "钱没收清也可以先充值：系统会自动记一笔「欠款充值订单」，之后把钱补上就行（补款）。",
							examples: []
						},
						{
							rule: "一张会员卡，同一时间只能有一笔欠款。欠款没补清之前，这张卡不能再充值。",
							examples: [
								"9 月 1 日会员充 5000 元只付了 3000 元（欠 2000 元）；9 月 3 日想再充 1000 元，系统会提示「该会员卡存在欠款的充值订单【单号】，请先补缴欠款后再充值！」；把 2000 元补清之后才能继续充值。"
							]
						},
						{
							rule: "除了欠款，只要这张卡上还有没确认的充值单，同样不能再充值。先把上一笔确认掉。",
							examples: [
								"上午刚给会员开了一笔充 1000 元的单子还没确认，下午又想再充一笔 → 系统会提示「存在未完成的会员充值记录」，先去把上午那笔确认掉。"
							]
						},
						{
							rule: "充值不能用「会员卡销」来付。充值是往卡里加钱，卡销是从卡里扣钱，这两件事不能混在一起，所以充值时支付方式里本来就选不到卡销。",
							examples: [
								"给会员充值 2000 元，支付方式只能选现金、微信、支付宝、银行卡这些，没有「会员卡销」这一项；只有买东西开销售订单时才能用卡销。"
							]
						},
						{
							rule: "欠款只有一个解决办法：补款。已经确认过的充值单不能修改、不能删除、也不能随充值退回。",
							examples: [
								"一笔充值单确认之后发现金额填错了 → 改不了、删不掉，只能走补款或退卡流程处理。"
							]
						}
					],
					tip: "口诀：一张卡、一笔欠款；想再充、先补清。充值只能往里加钱，不能用卡里的钱来充。"
				},
				{
					id: "sec-return",
					no: "05",
					title: "会员退卡、解绑与换卡",
					items: [
						{
							rule: "退卡前，系统会自动检查这张卡：还有没确认完的充值单 → 不能退；还有欠款充值订单 → 不能退，要先补缴。",
							examples: [
								"会员上午刚充了值还没确认，就来退卡 → 系统拒绝，先把那笔充值单确认或处理完。",
								"会员卡上挂着 2000 元充值欠款 → 系统拒绝退卡，补缴清欠款后才能退。"
							]
						},
						{
							rule: "退卡退的是卡里的余额。退款金额必须大于 0、且不能超过卡里现有的余额；卡里已经是 0 元的话，直接解绑就行，不用退。",
							examples: [
								"卡里剩 300 元，退款金额填 500 元 → 系统拒绝；填 300 元或更少（但要大于 0）都可以。"
							]
						},
						{
							rule: "整张卡退完之后余额会变成 0，之后才能给这个顾客换卡或者解绑会员卡。卡里还有钱时，系统会提示先退卡。",
							examples: [
								"会员说卡不要了、想换一张新卡 → 先把旧卡余额退掉（或消费完），才能换卡。"
							]
						},
						{
							rule: "换卡、解绑同样要过上面那两道检查：只要这张储值卡上还挂着未确认的充值单、或者欠款单，就不能换卡、不能解绑。",
							examples: [
								"会员卡上有一笔欠 1500 元的充值单，想直接换张新卡绕过 → 系统会拒绝，必须先补缴欠款。"
							]
						}
					],
					tip: "顺序记住：清掉未确认和欠款充值单 → 退卡（余额退到 0） → 换卡或解绑。"
				},
				{
					id: "sec-commission",
					no: "06",
					title: "提成规则",
					items: [
						{
							rule: "销售提成：按订单收费金额的 4% 计算。订单里用了会员卡销的，不算销售提成。",
							examples: [
								"一笔 2000 元的订单全部用会员卡销支付 → 没有销售提成；同样 2000 元的订单用现金支付 → 销售提成 2000 × 4% = 80 元。"
							]
						},
						{
							rule: "充值提成（按充值金额分档）：1000 元以内提 4%；1000 元（含）到 5000 元提 6%；5000 元及以上提 8%。",
							examples: [
								"充 800 元 → 提成 800 × 4% = 32 元；充 3000 元 → 提成 3000 × 6% = 180 元；充 8000 元 → 提成 8000 × 8% = 640 元。"
							]
						},
						{
							rule: "有欠款的充值订单，提成不在「确认」时生成：确认只把参与员工记到订单上，等欠款补足（补款完成）时才生成提成，提成时间按补足欠款的时间算。无欠款、确认即完成的充值订单，提成仍在确认时生成。",
							examples: [
								"9 月 1 日会员充 5000 元只付了 3000 元（欠 2000 元）并确认 → 当天不生成提成；9 月 10 日补缴 2000 元欠款 → 这时才生成 5000 × 8% = 400 元的充值提成，提成时间记为 9 月 10 日。",
								"一笔充值确认时钱已收齐（无欠款）→ 提成在确认当天生成，和原来一样。"
							]
						},
						{
							rule: "列表「是否可发放」列按「业务是否完成(IsFinished)」判断：业务已完成的提成标「可发放」，未完成的标「不可发放」——这和发放前提一致（业务未完成不能进发放环节）。列表同时保留「是否发放」列（是否已发放，由发放月份/发放时间体现）。发放时系统仍按关联业务单据的当前欠款状态「当场检测」：检测通过的当场发放；检测不通过的自动跳过并弹窗逐条列出（关联单号、提成类型、员工、金额、未发放原因），不影响其余发放。判定口径：① 充值提成关联「仍有欠款未补清」的充值订单 → 不通过；② 销售提成（订单销售提成、销售订单电话提成）关联「有欠款」的销售订单 → 不通过；③ 退单、退卡产生的冲正提成（金额为负）一律通过。筛选区「是否可发放」下拉即按 IsFinished 过滤。注意：系统已彻底移除 CanGrant 标志位及「批量处理历史数据」按钮，是否可发放仅由 IsFinished（业务是否完成）与发放时的当场检测决定。",
							examples: [
								"筛选「是否可发放=可发放」→ 列出业务已完成的提成；点发放时若其中某条关联的充值订单仍有欠款，系统当场拦截并弹窗列明细，其余照常发放。",
								"会员把欠款补清后直接点发放 → 系统当场检测到欠款已清，正常发放成功，不必先点其他按钮。"
							]
						},
						{
							rule: "防套现提醒：会员充完值当天就办卡销订单、并且卡余额降到 500 元以下的，之前那笔充值的提成会被取消，改按充值金额的 4% 重新计算。",
							examples: [
								"充 5000 元本来按 8% 提 400 元；当天就卡销把卡花到余额不足 500 元 → 这笔充值提成改为 5000 × 4% = 200 元。"
							]
						},
						{
							rule: "提成是在订单「确认」之后才生成的。订单还挂着没确认，提成就还没算，业绩也还没进统计。",
							examples: []
						}
					],
						tip: "充值提成鼓励大额充值；当天充值当天卡销花掉，提成会缩水。无欠款的充值提成在确认之后就算；有欠款的充值订单，提成在补足欠款之后才算。"
				},
				{
					id: "sec-cancel",
					no: "07",
					title: "退单规则",
					items: [
						{
							rule: "只有「已完成」的订单才能退单。确认之后钱还没收齐的订单（还挂着欠款），系统不允许退。",
							examples: [
								"一笔订单确认时只收了一部分钱、剩下的挂着欠条 → 这笔单子不能退，先把钱收齐再处理。"
							]
						},
						{
							rule: "退单会把钱原路退回：用会员卡付的部分会退回到卡里，卡里的余额和消费记录也会一起冲正。",
							examples: [
								"一笔 800 元的卡销订单退掉后，会员卡余额会加回 800 元，消费记录里会多一笔负数冲抵。"
							]
						},
						{
							rule: "还没确认的订单不用退，直接删除就行。",
							examples: []
						}
					],
					tip: "退单只针对已完成订单；未确认订单走删除，欠款未清的订单先收齐钱。"
				},
				{
					id: "sec-subscription",
					no: "08",
					title: "门店申购单（向采购部要货）",
					items: [
						{
							rule: "申购单是门店向采购部「要货」的单子。一张单子可以要多种商品，但同一种商品只能填一行。",
							examples: [
								"一张单子里把「隐形眼镜 A」填了两行 → 系统会提示「申购明细中存在相同的商品，请检查！」。想要更多就直接把数量改大。"
							]
						},
						{
							rule: "只能申购本门店库存里已经有的商品（也就是门店商品档案里有的）。从没进过的商品得先加到门店商品里。",
							examples: [
								"想申购一款门店从没卖过的镜片 → 系统会提示「申购的商品中有不存在门店库存中的商品，请检查！」。"
							]
						},
						{
							rule: "每张申购单都要走六步：待提交 → 采购待确认 → 待审核 → 待发快递 → 快递中 → 已完成。",
							examples: [
								"①门店新建并「提交」→ ②采购部填好成本和运费后「确认」→ ③审核人「审核通过」→ ④采购部录入快递单号 → ⑤门店收到货点「接收」→ ⑥状态变「已完成」，货才真正进库存。"
							]
						},
						{
							rule: "审核不通过不会被作废：状态会变回「审核不通过」，采购部改完后重新确认，就又回到「待审核」可以再送审。",
							examples: [
								"审核人觉得成本填高了，填了原因点不通过 → 采购部改成本后重新确认 → 单据回到「待审核」，再送一次审核即可。"
							]
						},
						{
							rule: "只有「待提交」状态能修改，「待提交」和「采购待确认」能删除。再往后就不能改也不能删了 —— 数量填错只能让审核人打回，或者等收货后走退货。",
							examples: [
								"提交后才发现数量填错了：如果还是「采购待确认」，直接删掉重开一张；如果已经被采购确认了，就联系审核人点「审核不通过」打回来。"
							]
						},
						{
							rule: "门店接收时填「实际收到几个」，实际数量不能超过申购数量；填 0 表示没到货，就不入库；没填快递单号的商品不能接收。",
							examples: [
								"申购 10 个只到了 8 个 → 实际数量填 8，库存就只加 8 个，剩下 2 个不会凭空进库存。",
								"某一行实际数量填了 11（超过申购的 10）→ 系统会提示「最多只允许接收【10】个」。"
							]
						},
						{
							rule: "财务付款在「审核通过」之后随时都能做（待发快递、快递中、已完成都行），付款不会改变单据状态，只是打上付款时间标记；付过一次就不能再付。",
							examples: [
								"货还没到就要先给供应商打款 → 单据在「待发快递」状态时就可以点付款，不影响后面正常发货和接收。"
							]
						},
						{
							rule: "退货只对「已完成」的申购单开放；退货数量既不能超过当时实际收到的数量，也不能超过门店现在的库存。",
							examples: [
								"申购 10 个、实际收到 10 个、已经卖掉 3 个 → 最多只能退 7 个，系统会按这两个数里较小的那个卡住。"
							]
						},
						{
							rule: "退货之后还有一条换货链：门店退货（库存立刻减少）→ 门店填快递单号发回 → 采购部收到 → 采购部发出换货 → 门店再次「接收」，这时换回的商品才回到库存。",
							examples: [
								"退了 2 个镜片，门店库存当场就少 2 个；半个月后采购部把新镜片寄回来，门店点了「接收」，库存才加回 2 个。中间这段时间库存是偏少的，这是正常的。"
							]
						}
					],
					tip: "记两句话：货在门店点「接收」那一刻才进库存；钱在审核通过之后随时能付，付款不改状态。"
				},
				{
					id: "sec-transfer",
					no: "09",
					title: "商品调拨（门店之间调货）",
					items: [
						{
							rule: "调拨就是门店之间互相调货，建单时要选「调出门店」和「调入门店」，单号以 PDB 开头。",
							examples: []
						},
						{
							rule: "保存调拨单的那一刻，调出门店的库存就立刻减掉了；调入门店要等点了「接收」之后才增加。中间这段时间货算「在途」，两边库存里都看不到。",
							examples: [
								"A 店调 5 个镜盒给 B 店：单子一保存，A 店库存当场少 5 个；B 店要等收到货点了「接收」，库存才多 5 个。"
							]
						},
						{
							rule: "调拨也是三步走：调出门店建单（库存立刻减）→ 确认 → 调入门店接收（库存才加）。必须先确认才能接收，两步都不能重复做。",
							examples: []
						},
						{
							rule: "⚠️ 有三种商品会被「跳过」不调拨，但现在系统会明确提示，不再悄悄丢失：①调出门店没有这个商品的库存记录 ②调入门店没有这个商品的档案 ③调出门店库存不够。保存调拨单后，凡被跳过的商品，web 端都会弹出一个提示框，逐条列出商品名称、规格、申请数量和未调拨原因（含具体门店与当前库存）。",
							examples: [
								"一张单子调 3 种商品，其中 1 种调入门店从来没卖过（没有商品档案）→ 保存后弹窗提示「有 1 种商品未调拨」，并列出该商品及原因「调入门店【B 店】无商品【…】的档案」。",
								"A 店库存只有 2 个，却填了调 5 个 → 弹窗提示该商品「调出门店【A 店】商品【…】库存不足（当前库存 2，申请调拨 5）」。"
							]
						},
						{
							rule: "所以保存之后如果出现「有 N 种商品未调拨」的提示，一定要点开看清单：被跳过的商品不会进调拨单，要补齐档案或库存后重新调拨。",
							examples: [
								"填了 4 种，提示「共调拨 3 种，有 1 种未调拨」→ 点开提示框，看少的那一种是什么原因（调入门店缺档案 / 调出门店库存不足）。"
							]
						},
						{
							rule: "如果所有商品都被跳过，系统会直接提示「调拨失败，没有检测成功需要调拨的商品，请检查后重试！」，这时一张调拨单都不会生成，调出门店的库存也不会变。",
							examples: []
						},
						{
							rule: "调拨数量必须大于 0，小于等于 0 会被拒绝。",
							examples: []
						},
						{
							rule: "还没确认、也没被接收的调拨单可以删除，删除后调出门店的库存会自动加回去。已经确认或者已经被接收的单子不能删。",
							examples: [
								"单子建错了还没点确认 → 直接删掉，A 店库存自动还原；如果已经确认了，就只能走反向调拨把货调回来。"
							]
						},
						{
							rule: "接收时如果调入门店缺这个商品的档案，接收会失败并提示「调入门店没有【…】信息」，需要先把商品加到调入门店再接收。",
							examples: []
						}
					],
					tip: "调拨的风险点是「部分商品被跳过」：数量对不上不会整体报错，只会少调几种。好在保存后 web 端会弹窗逐条列出被跳过的商品及原因，务必点开核对，确认无误再关掉。"
				},
				{
					id: "sec-statistic",
					no: "10",
					title: "统计口径小贴士",
					items: [
						{
							rule: "收款统计里的「实收」「现金」不包含会员卡销；卡销的金额会单独统计，方便对账。",
							examples: []
						},
						{
						rule: "会员卡的扣款、消费记录，都在订单「确认」那一刻生成，日期也按确认时间算。",
						examples: []
					},
					{
						rule: "「订单统计」里的「出入库统计」页签：按【上线商品类型(镜片类型)】×【年度】统计 1~12 月入库数量与出库数量，时间取实际入库时间(InputDate)/出库时间(OutputDate)，全部门店汇总；表格每行一个上线商品类型，每月下分「入/出」两子列，末尾为全年合计，并带合计行。切换年份即按该年实际出入库重算。",
						examples: []
					}
				]
				},
				{
					id: "sec-faq",
					no: "11",
					title: "常见提示速查表",
					items: [],
					table: [
						{
							msg: "储值卡可用余额【X元】不足，请选择其他支付方式！",
							how: "这张卡现在能花的钱不够。如果有欠款，可用余额会比卡余额少。办法：让会员先补缴欠款、改用其他支付方式，或者拆成几笔付。"
						},
						{
							msg: "该会员卡存在未确认的卡销订单【单号】，请先确认或取消该订单后再下单！",
							how: "这张卡上有一笔用卡销开的单子还没确认，占着位置。去销售订单列表把那笔单确认掉、删掉，或者把卡销改成别的支付方式。"
						},
						{
							msg: "会员存在未完成订单【单号】，请先完成该订单！",
							how: "这个会员名下还有没确认的订单（不一定是用会员卡付的）。先把那笔确认掉，才能开新的卡销单。"
						},
						{
							msg: "会员存在相同金额的未完成订单【单号】，请先完成该订单！",
							how: "今天已经开过一笔金额一模一样、还没确认的单子。把上一笔确认掉，或者把金额改成不一样的。"
						},
						{
							msg: "会员卡销仅能支付一笔！",
							how: "一笔订单里卡销只能填一行。把两行卡销合并成一行。"
						},
						{
							msg: "同一种支付方式只允许使用一次！",
							how: "现金、微信等同一种方式在一笔订单里只能出现一行。把重复的两行合并，或者改用组合支付。"
						},
						{
							msg: "实际收款金额不能超过订单金额！",
							how: "支付明细里收的钱比订单金额多了。一行一行核对支付金额。"
						},
						{
							msg: "支付金额不能为0",
							how: "至少要填一笔支付方式，而且金额要大于 0。"
						},
						{
							msg: "该会员卡存在欠款的充值订单【单号】，请先补缴欠款后再充值！",
							how: "这张卡上一笔充值还没收齐钱。先做「补款」把欠款结清，才能再充值。"
						},
						{
							msg: "存在未完成的会员充值记录，不允许再次充值！",
							how: "有一笔充值单还没确认。先去把它确认掉，再充下一笔。"
						},
						{
							msg: "充值不允许使用会员卡销支付！",
							how: "充值不能用会员卡里的钱来付。换成现金、微信、支付宝或银行卡。"
						},
						{
							msg: "该储值卡存在未确认的充值订单【单号】，不允许退卡 / 换卡 / 解绑！",
							how: "先把那笔没确认的充值单确认掉，再来做退卡、换卡或解绑。"
						},
						{
							msg: "该储值卡存在欠款的充值订单【单号】，请先补缴欠款后再退卡 / 换卡 / 解绑！",
							how: "先补缴欠款，再做退卡、换卡或解绑。"
						},
						{
							msg: "会员充值记录已经确认，不允许修改 / 删除！",
							how: "确认过的充值单改不了、也删不掉。金额填错只能走补款或退卡流程。"
						},
						{
							msg: "退卡金额必须大于0且不能超过储值卡余额！",
							how: "核对一下卡里的实际余额，重填退款金额。"
						},
						{
							msg: "储值卡余额为0，无需退卡！",
							how: "卡里已经没钱了，不需要退卡，直接解绑或换卡即可。"
						},
						{
							msg: "会员充值记录未完成，不允许退卡，可以直接删除！",
							how: "这笔充值单还没确认，不用走退卡，直接删掉就行。"
						},
						{
							msg: "会员余额不足，无法退卡！",
							how: "卡里的余额不够退。核对实际余额后再操作。"
						},
						{
							msg: "申购明细中存在相同的商品，请检查！",
							how: "同一种商品在一张申购单里只能填一行。把重复的两行合并，直接改数量就行。"
						},
						{
							msg: "申购的商品中有不存在门店库存中的商品，请检查！",
							how: "只能申购门店库存里已经有的商品。从没卖过的要先在门店商品里加上。"
						},
						{
							msg: "申购的商品数量必须大于0，请检查！",
							how: "明细里某一行数量填了 0 或空，改成实际要货数量。"
						},
						{
							msg: "当前申购单状态不允许修改！",
							how: "只有「待提交」状态能改。已经被提交的单子要改，只能删除重开，或者让审核人打回来。"
						},
						{
							msg: "当前申购单状态不允许删除！",
							how: "只有「待提交」和「采购待确认」能删。再往后（待审核及之后）就删不掉了。"
						},
						{
							msg: "当前申购单状态不允许接收！",
							how: "只有「快递中」能点接收。先让采购部把快递单号录进去。"
						},
						{
							msg: "存在未填写实际数量的申购单明细，不允许接收！",
							how: "每一行的「实际数量」都要填，确实没到货就填 0。"
						},
						{
							msg: "商品信息：【名称；规格】最多只允许接收【N】个！",
							how: "实际收货数量不能超过申购数量。多到的部分要另外补一张申购单。"
						},
						{
							msg: "商品信息：【名称；规格】未有快递信息，不允许接收！",
							how: "这一行还没有快递单号，让采购部先录入单号再接收。"
						},
						{
							msg: "当前申购单状态不允许财务付款！",
							how: "要等审核通过之后（待发快递 / 快递中 / 已完成）才能付款。"
						},
						{
							msg: "当前申购单已经财务付款，不允许重复操作！",
							how: "这笔已经付过款了，不用再付一次。"
						},
						{
							msg: "申购单未完成，不允许退货！",
							how: "只有「已完成」的申购单能退。货还没到就先别退，等接收完再退。"
						},
						{
							msg: "申购的商品【名称；规格】最多只允许退【N】个！",
							how: "退货数量不能超过当时实际收到的数量。"
						},
						{
							msg: "申购的商品【名称；规格】，库存数量只有N个，最多只允许退【N】个！",
							how: "退货数量不能超过门店现在的库存。已经卖掉的部分要先补回来才能退。"
						},
						{
							msg: "调拨失败，没有检测成功需要调拨的商品，请检查后重试！",
							how: "所有商品都被跳过了。常见原因：调入门店没有这些商品的档案、调出门店库存不够。检查后重试。"
						},
						{
							msg: "调拨的商品数量不能小于等于0！",
							how: "明细里某一行数量填了 0，改成实际调拨数量。"
						},
						{
							msg: "调拨记录已确认，不允许删除！ / 调拨记录已被调入门店接收，不允许删除！",
							how: "确认过或被接收过就不能删了。要退回就做一张反向调拨单。"
						},
						{
							msg: "调拨记录未确认，不允许操作接收！",
							how: "调入门店要等调出门店先点「确认」之后才能接收。"
						},
						{
							msg: "调拨记录已被调入门店接收，请勿重复接收！",
							how: "这笔已经接收过了，库存早就加上了，不要重复点。"
						},
						{
							msg: "调入门店没有【类型：…;名称：…；规格：…】信息！",
							how: "接收时调入门店缺这个商品的档案。先把商品加到调入门店，再来接收。"
						}
					],
					tip: "提示里带的【单号】就是出问题的那笔单子。复制单号去对应的列表页搜索，就能直接找到它。"
				}
			]
		};
	},
	mounted() {
		this.bindScroll();
		this.$nextTick(this.syncActiveSection);
	},

	// 页面被 keepAlive 缓存，切回标签页时重新对齐一次高亮
	activated() {
		this.$nextTick(this.syncActiveSection);
	},

	watch: {
		// 窗口很矮时目录自身会滚动，保证当前项始终可见
		activeSection() {
			this.$nextTick(this.scrollTocIntoView);
		}
	},

	beforeDestroy() {
		this.unbindScroll();
	},

	methods: {
		bindScroll() {
			const box = this.$refs.contentBox;
			if (box) {
				box.addEventListener("scroll", this.handleScroll, { passive: true });
			}
			window.addEventListener("resize", this.handleScroll);
		},

		unbindScroll() {
			const box = this.$refs.contentBox;
			if (box) {
				box.removeEventListener("scroll", this.handleScroll);
			}
			window.removeEventListener("resize", this.handleScroll);
			if (this.manualTimer) {
				clearTimeout(this.manualTimer);
				this.manualTimer = null;
			}
		},

		// 节流：一帧最多同步一次高亮
		handleScroll() {
			if (this.scrollTicking) {
				return;
			}
			this.scrollTicking = true;
			window.requestAnimationFrame(() => {
				this.scrollTicking = false;
				this.syncActiveSection();
			});
		},

		// 取章节元素：限定在当前内容区内查找，避免多标签页时 id 撞车
		getSectionEl(id) {
			const box = this.$refs.contentBox;
			return box ? box.querySelector("#" + id) : null;
		},

		// 由内容区的滚动位置，反推当前应该高亮哪一节
		syncActiveSection() {
			const box = this.$refs.contentBox;
			if (!box) {
				return;
			}
			if (this.isManualScroll) {
				return;
			}

			// 已经滚到底：直接高亮最后一节。
			// 否则最后一节可能因为高度不够，永远到不了判定线，导致高亮卡在倒数第二节。
			if (box.scrollTop + box.clientHeight >= box.scrollHeight - 4) {
				this.activeSection = this.sections[this.sections.length - 1].id;
				return;
			}

			// 判定线：容器顶部往下 24px，章节顶边越过它就认为「进入这一节」
			const line = box.getBoundingClientRect().top + 24;
			let current = this.sections[0].id;
			for (let i = 0; i < this.sections.length; i++) {
				const el = this.getSectionEl(this.sections[i].id);
				if (!el) {
					continue;
				}
				if (el.getBoundingClientRect().top <= line) {
					current = this.sections[i].id;
				} else {
					break;
				}
			}
			this.activeSection = current;
		},

		scrollTo(id) {
			const box = this.$refs.contentBox;
			const el = this.getSectionEl(id);
			if (!box || !el) {
				return;
			}

			this.activeSection = id;
			// 手动跳转期间暂停反推，等平滑滚动结束再恢复
			this.isManualScroll = true;
			if (this.manualTimer) {
				clearTimeout(this.manualTimer);
			}
			this.manualTimer = setTimeout(() => {
				this.isManualScroll = false;
				this.manualTimer = null;
				this.syncActiveSection();
			}, 600);

			const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 12;
			box.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
		},

		scrollTocIntoView() {
			const toc = this.$refs.tocBox;
			if (!toc) {
				return;
			}
			const active = toc.querySelector(".guide-toc__item--active");
			if (!active) {
				return;
			}
			const top = active.offsetTop;
			const bottom = top + active.offsetHeight;
			if (top < toc.scrollTop) {
				toc.scrollTop = top;
			} else if (bottom > toc.scrollTop + toc.clientHeight) {
				toc.scrollTop = bottom - toc.clientHeight;
			}
		},

		// 回到顶部（滚动发生在内容区内部，不是 body）
		backToTop() {
			this.activeSection = this.sections[0].id;
			const box = this.$refs.contentBox;
			if (box) {
				box.scrollTo({ top: 0, behavior: "smooth" });
			}
		}
	}
};
</script>

<style scoped>
/* 页面不写死高度：直接贴合标签页容器（.router-tab__container，position:relative），
   用 absolute 四边定位而不是 height:100% —— 中间还有缓存层会打断百分比高度链。
   页面内部再用 flex 分配：页头固定，右侧正文吃满剩余空间并自己滚动
   （外层容器是 overflow:hidden，靠外层滚动是滚不动的） */
.guide-page {
	box-sizing: border-box;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

/* 页头 */
.guide-hero {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	padding: 22px 26px;
	border-radius: 12px;
	background: linear-gradient(90deg, #16307b 0%, #1e40af 100%);
	color: #fff;
	box-shadow: 0 6px 18px rgba(30, 64, 175, 0.22);
}

.guide-hero__icon {
	width: 52px;
	height: 52px;
	border-radius: 12px;
	background: rgba(255, 255, 255, 0.16);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 28px;
	margin-right: 18px;
	flex-shrink: 0;
}

.guide-hero__title {
	font-size: 20px;
	font-weight: 600;
	letter-spacing: 1px;
}

.guide-hero__sub {
	margin-top: 6px;
	font-size: 13px;
	line-height: 1.7;
	color: rgba(255, 255, 255, 0.85);
	max-width: 900px;
}

/* 布局：目录固定宽度，剩余空间全给正文 */
.guide-body {
	display: flex;
	align-items: stretch;
	flex: 1 1 auto;
	min-height: 0;
	margin-top: 18px;
	gap: 18px;
}

/* 左侧目录：独立成列，不随正文滚动；条目过多时自身滚动 */
.guide-toc {
	/* 定位上下文：让目录项的 offsetTop 相对本容器计算（scrollTocIntoView 依赖） */
	position: relative;
	width: 190px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	background: #fff;
	border: 1px solid #ebeef5;
	border-radius: 10px;
	padding: 12px 8px;
	box-sizing: border-box;
}

.guide-toc__title {
	font-size: 12px;
	color: #909399;
	padding: 0 10px 8px;
	border-bottom: 1px solid #f0f2f5;
	margin-bottom: 6px;
}

.guide-toc__item {
	position: relative;
	display: flex;
	align-items: center;
	padding: 8px 10px;
	border-radius: 8px;
	font-size: 13px;
	color: #475569;
	cursor: pointer;
	flex-shrink: 0;
	transition: all 0.2s ease;
}

.guide-toc__backtop {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: auto;
	padding: 8px 10px;
	border-top: 1px solid #f0f2f5;
	font-size: 12px;
	color: #64748b;
	cursor: pointer;
	flex-shrink: 0;
}

.guide-toc__backtop:hover {
	color: #1e40af;
}

.guide-toc__backtop i {
	margin-right: 5px;
	font-size: 13px;
}

.guide-toc__item:hover {
	background: #eef5ff;
	color: #1e40af;
}

/* 当前所在章节：左边加一条蓝色指示条，滚动时随内容自动切换 */
.guide-toc__item--active {
	background: #e8f0ff;
	color: #1e40af;
	font-weight: 600;
}

.guide-toc__item--active::before {
	content: "";
	position: absolute;
	left: 0;
	top: 50%;
	transform: translateY(-50%);
	width: 3px;
	height: 16px;
	border-radius: 0 3px 3px 0;
	background: #1e40af;
}

.guide-toc__num {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: #eef2f7;
	color: #64748b;
	font-size: 11px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 8px;
	flex-shrink: 0;
}

.guide-toc__item--active .guide-toc__num,
.guide-toc__item:hover .guide-toc__num {
	background: #1e40af;
	color: #fff;
}

/* 正文：吃掉剩余空间（不写死高度），内容超出就在这里滚动 */
.guide-content {
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 6px;
	padding-bottom: 40px;
}

.guide-content::-webkit-scrollbar {
	width: 8px;
}

.guide-content::-webkit-scrollbar-thumb {
	background: #d4dae3;
	border-radius: 4px;
}

.guide-content::-webkit-scrollbar-thumb:hover {
	background: #b9c2ce;
}

.guide-content::-webkit-scrollbar-track {
	background: transparent;
}

.guide-section {
	background: #fff;
	border: 1px solid #ebeef5;
	border-radius: 10px;
	padding: 18px 22px;
	margin-bottom: 16px;
	scroll-margin-top: 12px;
}

.guide-section__head {
	display: flex;
	align-items: center;
	padding-bottom: 12px;
	margin-bottom: 14px;
	border-bottom: 1px solid #f0f2f5;
}

.guide-section__no {
	font-size: 12px;
	font-weight: 700;
	color: #1e40af;
	background: #e8f0ff;
	border-radius: 6px;
	padding: 3px 8px;
	margin-right: 10px;
}

.guide-section__title {
	font-size: 16px;
	font-weight: 600;
	color: #1f2d3d;
}

/* 条款 */
.guide-clause {
	padding: 10px 0;
}

.guide-clause + .guide-clause {
	border-top: 1px dashed #eef1f6;
}

.guide-clause__rule {
	display: flex;
	align-items: flex-start;
	font-size: 14px;
	color: #1f2d3d;
	line-height: 1.8;
}

.guide-clause__icon {
	color: #22a06b;
	font-size: 15px;
	margin: 4px 8px 0 0;
	flex-shrink: 0;
}

.guide-clause__example {
	display: flex;
	align-items: flex-start;
	margin: 8px 0 0 23px;
	padding: 9px 12px;
	background: #fffbe8;
	border: 1px solid #ffe9b0;
	border-left: 3px solid #f5b517;
	border-radius: 6px;
	font-size: 13px;
	color: #6b5a1e;
	line-height: 1.7;
}

.guide-clause__example-tag {
	flex-shrink: 0;
	font-size: 12px;
	color: #a16207;
	background: #fef3c7;
	border-radius: 4px;
	padding: 1px 7px;
	margin-right: 9px;
	margin-top: 1px;
	font-weight: 600;
}

/* 常见提示速查表 */
.guide-table {
	margin-top: 10px;
	border: 1px solid #ebeef5;
	border-radius: 8px;
	overflow: hidden;
}

.guide-table__row {
	display: flex;
	border-bottom: 1px solid #f0f2f5;
}

.guide-table__row:last-child {
	border-bottom: none;
}

.guide-table__row--head {
	background: #f5f7fa;
}

.guide-table__row--head .guide-table__cell {
	font-weight: 600;
	color: #303133;
}

.guide-table__cell {
	box-sizing: border-box;
	padding: 10px 14px;
	font-size: 13px;
	line-height: 1.7;
	color: #475569;
}

.guide-table__cell--no {
	width: 58px;
	flex-shrink: 0;
	text-align: center;
	color: #94a3b8;
	font-weight: 600;
	border-right: 1px solid #f0f2f5;
}

.guide-table__cell:last-child {
	flex: 1 1 auto;
	min-width: 0;
}

.guide-table__cell--msg {
	width: 38%;
	flex-shrink: 0;
	border-right: 1px solid #f0f2f5;
	color: #b45309;
	font-weight: 600;
}

/* 段落提示 */
.guide-section__tip {
	display: flex;
	align-items: flex-start;
	margin-top: 12px;
	padding: 10px 14px;
	background: #f0f7ff;
	border: 1px solid #d6e6ff;
	border-radius: 8px;
	font-size: 13px;
	color: #1e40af;
	line-height: 1.7;
}

.guide-section__tip i {
	margin: 3px 8px 0 0;
	flex-shrink: 0;
}

.guide-footer {
	margin-top: 6px;
	text-align: center;
	font-size: 12px;
	color: #94a3b8;
	line-height: 1.8;
	padding-bottom: 10px;
}

/* 窄屏隐藏目录 */
@media (max-width: 900px) {
	.guide-toc {
		display: none;
	}
}
</style>
